import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Cog6ToothIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  UsersIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { SettingsProvider } from '../contexts/SettingsContext';
import SystemSettingsForm from '../components/settings/SystemSettingsForm';
import ContactInformationForm from '../components/settings/ContactInformationForm';
import BankInformationForm from '../components/settings/BankInformationForm';
import UserRolesForm from '../components/settings/UserRolesForm';
import UserManagement from '../components/settings/UserManagement';
import CommunicationSettingsForm from '../components/settings/CommunicationSettingsForm';
import LegalDocumentsForm from '../components/settings/LegalDocumentsForm';
import EmailSettingsForm from '../components/settings/EmailSettingsForm';
import RegistrationSettingsForm from '../components/settings/RegistrationSettingsForm';
import GoogleAnalyticsForm from '../components/settings/GoogleAnalyticsForm';
import MobileAppSettingsForm from '../components/settings/MobileAppSettingsForm';
import ScorerAccountsManager from '../components/settings/ScorerAccountsManager';
import PreviewEnvironments from './PreviewEnvironments';

type SettingsTab = 'system' | 'registration' | 'contact' | 'roles' | 'users' | 'scorers' | 'communications' | 'legal' | 'email' | 'analytics' | 'mobile_app' | 'preview';

export default function Settings() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('system');

  const renderTabContent = () => {
    switch (activeTab) {
      case 'system':
        return <SystemSettingsForm />;
      case 'registration':
        return <RegistrationSettingsForm />;
      case 'contact':
        return (
          <div className="space-y-8">
            <ContactInformationForm />
            <BankInformationForm />
          </div>
        );
      case 'roles':
        return <UserRolesForm />;
      case 'users':
        return <UserManagement />;
      case 'scorers':
        return <ScorerAccountsManager />;
      case 'communications':
        return <CommunicationSettingsForm />;
      case 'legal':
        return <LegalDocumentsForm />;
      case 'email':
        return <EmailSettingsForm />;
      case 'analytics':
        return <GoogleAnalyticsForm />;
      case 'mobile_app':
        return <MobileAppSettingsForm />;
      case 'preview':
        return <PreviewEnvironments />;
      default:
        return <SystemSettingsForm />;
    }
  };

  return (
    <SettingsProvider>
      <div className="p-6">
        <h1 className="text-2xl font-bold text-golf-gray mb-6">Settings</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <nav className="flex flex-col">
                <button
                  onClick={() => setActiveTab('system')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'system'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <Cog6ToothIcon className="w-5 h-5" />
                  <span className="font-medium">System Settings</span>
                </button>

                <button
                  onClick={() => setActiveTab('registration')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'registration'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <ClipboardDocumentCheckIcon className="w-5 h-5" />
                  <span className="font-medium">Registration</span>
                </button>

                <button
                  onClick={() => setActiveTab('contact')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'contact'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <BuildingOfficeIcon className="w-5 h-5" />
                  <span className="font-medium">Contact & Banking</span>
                </button>
                
                <button
                  onClick={() => setActiveTab('roles')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'roles'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <UserGroupIcon className="w-5 h-5" />
                  <span className="font-medium">User Roles</span>
                </button>

                <button
                  onClick={() => setActiveTab('users')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'users'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <UsersIcon className="w-5 h-5" />
                  <span className="font-medium">Users</span>
                </button>

                <button
                  onClick={() => setActiveTab('scorers')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'scorers'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <ClipboardDocumentListIcon className="w-5 h-5" />
                  <span className="font-medium">Scorer Accounts</span>
                </button>

                <button
                  onClick={() => setActiveTab('communications')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'communications'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <EnvelopeIcon className="w-5 h-5" />
                  <span className="font-medium">Communications</span>
                </button>
                
                <button
                  onClick={() => setActiveTab('email')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'email'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <EnvelopeIcon className="w-5 h-5" />
                  <span className="font-medium">Email Settings</span>
                </button>
                
                <button
                  onClick={() => setActiveTab('legal')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'legal'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <DocumentTextIcon className="w-5 h-5" />
                  <span className="font-medium">Legal Documents</span>
                </button>

                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'analytics'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <ChartBarIcon className="w-5 h-5" />
                  <span className="font-medium">Analytics</span>
                </button>

                <button
                  onClick={() => setActiveTab('mobile_app')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'mobile_app'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <DevicePhoneMobileIcon className="w-5 h-5" />
                  <span className="font-medium">Mobile App</span>
                </button>

                <button
                  onClick={() => setActiveTab('preview')}
                  className={`flex items-center space-x-3 px-4 py-3 text-left ${
                    activeTab === 'preview'
                      ? 'bg-golf-orange/10 border-l-4 border-golf-orange text-golf-orange'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <ComputerDesktopIcon className="w-5 h-5" />
                  <span className="font-medium">Preview</span>
                </button>
              </nav>
            </div>
          </div>
          
          {/* Main Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:col-span-3"
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </div>
    </SettingsProvider>
  );
}